"use client";

import { useEffect, useState } from "react";
import {
  LogOut,
  InfoIcon,
  Menu,
  Calendar,
  Heart,
  MessageSquare,
  UserCircle2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { signOutAction } from "@/app/actions/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useUnreadChatCount } from "@/lib/realtime/unread-chat-store";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

interface UserMenuProps {
  onLoginClick: () => void;
}

export default function UserMenu({ onLoginClick }: UserMenuProps) {
  // 로그인 상태의 단일 출처는 AuthProvider — 여기서 따로 getCurrentUser() 를 부르지 않는다.
  const { status, user } = useAuth();
  const loading = status === "loading";
  const [hasUnread, setHasUnread] = useState(false);
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("common");

  // unread dot은 공유 스토어의 단일 realtime 구독을 같이 쓴다 — BottomNavBar와
  // 별도 채널을 만들면 유저당 전역 구독이 2배가 되어 동시접속 급증 시 realtime
  // RLS 평가로 DB CPU를 태운다 (2026-06-12 콘서트 장애).
  const unreadCount = useUnreadChatCount(user?.id);

  useEffect(() => {
    setHasUnread(unreadCount > 0);
  }, [unreadCount]);

  const handleSignOut = async () => {
    try {
      await signOutAction();
      toast.success(t("userMenu.logoutSuccess"), {
        description: t("userMenu.logoutSuccessDesc"),
      });
      window.location.reload();
    } catch {
      toast.error(t("userMenu.logoutFailed"), {
        description: t("userMenu.logoutFailedDesc"),
      });
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <button className="bg-gray-100 rounded-full transition p-3">
        <Menu className="w-4 h-4 text-gray-700" />
      </button>
    );
  }

  // 로그인하지 않은 경우
  if (!user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="bg-gray-100 hover:bg-gray-200 rounded-full transition p-3">
            <Menu className="w-4 h-4 text-gray-700" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 p-3 px-0 rounded-xl">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => router.push(`/${locale}/help`)}
          >
            <InfoIcon className="mr-2 w-4 h-4" />
            <span>{t("userMenu.helpCenter")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onClick={onLoginClick}>
            {t("auth.loginOrSignup")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // 로그인한 경우
  const initials = user.full_name
    ? user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative hover:bg-gray-100 rounded-full transition">
          <Avatar className="w-10 h-10">
            <AvatarFallback className="bg-gray-800 text-white text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          {hasUnread && (
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-3 px-0 rounded-xl">
        <DropdownMenuLabel className="pb-2">
          <p className="text-sm font-medium truncate">
            {user.full_name || user.email.split("@")[0]}
          </p>
          <p className="text-xs font-normal text-muted-foreground truncate">
            {user.email}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => router.push(`/${locale}/profile/wishlist`)}
        >
          <Heart className="mr-2 h-4 w-4" />
          <span>{t("nav.wishlist")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => router.push(`/${locale}/profile/reservations`)}
        >
          <Calendar className="mr-2 h-4 w-4" />
          <span>{t("userMenu.reservations")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => router.push(`/${locale}/messages`)}
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          <span className="flex-1">{t("nav.messages")}</span>
          {hasUnread && (
            <span className="shrink-0 w-2.5 h-2.5 bg-red-500 rounded-full" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => router.push(`/${locale}/profile`)}
        >
          <UserCircle2 className="mr-2 h-4 w-4" />
          <span>{t("nav.profile")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => router.push(`/${locale}/help`)}
        >
          <InfoIcon className="mr-2 h-4 w-4" />
          <span>{t("userMenu.helpCenter")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("nav.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
